import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, useCurrency, API } from "../../App";
import UserLayout from "../../components/UserLayout";
import { Button } from "../../components/ui/button";
import { Wallet, Gift, Loader2, CheckCircle, AlertTriangle, MapPin, ArrowDownLeft, ArrowUpRight, Download, Receipt, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useRazorpay } from "react-razorpay";
import { downloadInvoicePdf } from "../../utils/pdfDownload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserWallet() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const { symbol } = useCurrency();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [hasBilling, setHasBilling] = useState(null);
  const [hasDelivery, setHasDelivery] = useState(null);
  const { Razorpay } = useRazorpay();
  const [activeTab, setActiveTab] = useState("plans");
  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(false);

  useEffect(() => { fetchPlans(); checkBillingAddress(); checkDeliveryAddress(); }, [location.pathname]);

  // Re-check addresses when user navigates back from profile
  useEffect(() => {
    const handleFocus = () => { checkBillingAddress(); checkDeliveryAddress(); };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", () => { if (!document.hidden) handleFocus(); });
    return () => { window.removeEventListener("focus", handleFocus); };
  }, []);

  useEffect(() => {
    if (activeTab === "history" && transactions.length === 0) fetchTransactions();
  }, [activeTab]);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/plans`, { credentials: "include" });
      if (response.ok) setPlans(await response.json());
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const checkBillingAddress = async () => {
    try {
      const res = await fetch(`${API}/billing-address`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setHasBilling(!!(data && data.address_line));
      } else {
        setHasBilling(false);
      }
    } catch { setHasBilling(false); }
  };

  const checkDeliveryAddress = async () => {
    try {
      const res = await fetch(`${API}/addresses`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setHasDelivery(Array.isArray(data) && data.length > 0);
      } else {
        setHasDelivery(false);
      }
    } catch { setHasDelivery(false); }
  };

  const fetchTransactions = async () => {
    setTxnLoading(true);
    try {
      const res = await fetch(`${API}/wallet/transactions`, { credentials: "include" });
      if (res.ok) setTransactions(await res.json());
    } catch (error) { console.error(error); }
    finally { setTxnLoading(false); }
  };

  const handleRecharge = async (plan) => {
    if (!hasDelivery) {
      toast.error("Please add a delivery address in your profile first");
      navigate("/profile");
      return;
    }
    if (!hasBilling) {
      toast.error("Please add a billing address in your profile first");
      navigate("/profile");
      return;
    }
    setSelectedPlan(plan.plan_id);
    try {
      const response = await fetch(`${API}/wallet/recharge`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ plan_id: plan.plan_id }),
      });
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || "Failed to initiate recharge"); }
      openRazorpayCheckout(await response.json());
    } catch (error) { toast.error(error.message); setSelectedPlan(null); }
  };

  const isWebView = () => !!(window.Capacitor || window.AndroidBridge || window.NativeBridge);

  const openRazorpayCheckout = async (orderData) => {
    if (isWebView()) {
      // Native Razorpay SDK — supports UPI Intent natively
      try {
        const options = {
          key: orderData.key_id,
          amount: String(orderData.amount),
          currency: orderData.currency,
          name: "Super Morsel",
          description: `Wallet Recharge - ${orderData.plan_name}`,
          order_id: orderData.order_id,
          prefill: { name: orderData.user_name || "", email: orderData.user_email || "", contact: orderData.user_phone || "" },
          theme: { color: "#16a34a" },
        };
        // Use native Capacitor Razorpay plugin
        const Checkout = window.Capacitor?.Plugins?.Checkout;
        if (Checkout) {
          const result = await Checkout.open(options);
          console.log("Native Razorpay result:", JSON.stringify(result));
          // Native plugin may return response in different formats:
          // { razorpay_payment_id, razorpay_order_id, razorpay_signature }
          // OR nested: { response: { razorpay_payment_id, ... } }
          const paymentData = result?.response || result;
          const paymentId = paymentData?.razorpay_payment_id || paymentData?.["razorpay_payment_id"];
          const orderId = paymentData?.razorpay_order_id || paymentData?.["razorpay_order_id"] || orderData.order_id;
          const signature = paymentData?.razorpay_signature || paymentData?.["razorpay_signature"];

          if (paymentId) {
            await verifyPayment({
              razorpay_payment_id: paymentId,
              razorpay_order_id: orderId,
              razorpay_signature: signature,
            });
          } else {
            console.error("No payment_id in result:", result);
            setSelectedPlan(null);
            toast.error("Payment verification failed - no payment ID received");
          }
        } else {
          // Fallback: native plugin not available, use web checkout
          openWebCheckout(orderData);
        }
      } catch (err) {
        console.error("Native Razorpay error:", err);
        setSelectedPlan(null);
        if (err?.description) {
          toast.error(err.description);
        } else if (err?.message?.includes("cancelled") || err?.code === 2) {
          toast.info("Payment cancelled");
        } else {
          toast.error("Payment failed. Please try again.");
        }
      }
    } else {
      // Web browser — use react-razorpay as before
      openWebCheckout(orderData);
    }
  };

  const openWebCheckout = (orderData) => {
    const options = {
      key: orderData.key_id, amount: orderData.amount, currency: orderData.currency,
      name: "Super Morsel", description: `Wallet Recharge - ${orderData.plan_name}`, order_id: orderData.order_id,
      handler: async (response) => { await verifyPayment(response); },
      prefill: { name: orderData.user_name || "", email: orderData.user_email || "", contact: orderData.user_phone || "" },
      theme: { color: "#16a34a" },
      modal: { ondismiss: () => { setSelectedPlan(null); toast.info("Payment cancelled"); } },
    };
    const rzpInstance = new Razorpay(options);
    rzpInstance.on("payment.failed", () => { setSelectedPlan(null); toast.error("Payment failed"); });
    rzpInstance.open();
  };

  const verifyPayment = async (razorpayResponse) => {
    console.log("Verifying payment:", JSON.stringify(razorpayResponse));
    try {
      const response = await fetch(`${API}/wallet/verify-payment`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ razorpay_order_id: razorpayResponse.razorpay_order_id, razorpay_payment_id: razorpayResponse.razorpay_payment_id, razorpay_signature: razorpayResponse.razorpay_signature }),
      });
      const data = await response.json();
      console.log("Verify response:", response.status, JSON.stringify(data));
      if (!response.ok) throw new Error(data?.detail || "Verification failed");
      if (data.payment_status === "paid" || data.status === "complete" || data.status === "already_processed") {
        setPaymentSuccess(Math.round(data.credits_added)); await refreshUser(); toast.success(`${Math.round(data.credits_added)} credits added!`);
        fetchTransactions();
      }
    } catch (err) { console.error("Verify error:", err); toast.error("Verification failed. Credits will be added automatically if payment was successful."); }
    finally { setSelectedPlan(null); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "";
  const formatTime = (d) => d ? new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
  const getMonthYear = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "Unknown";

  const groupByMonth = (txns) => {
    const groups = {};
    txns.forEach((t) => {
      const key = getMonthYear(t.created_at);
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return Object.entries(groups);
  };

  const getTypeLabel = (type) => {
    const labels = {
      self_recharge: "Self Recharge",
      admin_load: "Admin Credit Load",
      order_spend: "Order Payment",
      cashout: "Cash Out",
      refund: "Refund",
      cancelled: "Order Cancelled - Refund",
      wallet_blocked: "Wallet Blocked",
      wallet_unblocked: "Wallet Unblocked",
    };
    return labels[type] || type.replace(/_/g, " ");
  };

  const getTypeColor = (type, amount) => {
    if (amount > 0) return "text-green-600";
    if (amount < 0) return "text-red-600";
    return "text-gray-500";
  };

  const getTypeBg = (amount) => {
    if (amount > 0) return "bg-green-100";
    if (amount < 0) return "bg-red-100";
    return "bg-gray-100";
  };

  return (
    <UserLayout>
      <div className="p-4 animate-fade-in" data-testid="user-wallet">
        <div className="wallet-card mb-6">
          <div className="relative z-10">
            <p className="text-[10px] uppercase tracking-widest text-green-100 font-semibold mb-1">Available Balance</p>
            <div className="flex items-center gap-3 mb-1">
              <p className="text-4xl font-black" data-testid="wallet-balance">{symbol}{Math.round(user?.wallet_balance || 0)}</p>
              <button
                onClick={async (e) => { const btn = e.currentTarget; btn.classList.add("animate-spin"); await refreshUser(); fetchTransactions(); setTimeout(() => btn.classList.remove("animate-spin"), 600); }}
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                data-testid="wallet-refresh-balance"
              >
                <RefreshCw size={14} />
              </button>
            </div>
            <p className="text-xs text-green-200">Use credits to order meals</p>
          </div>
        </div>

        {paymentSuccess && (
          <div className="flex items-center gap-3 p-4 mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-xl animate-slide-up" data-testid="payment-success-banner">
            <CheckCircle size={20} className="text-green-500" />
            <div><p className="text-sm font-bold text-green-700 dark:text-green-400">+{paymentSuccess} credits added!</p></div>
            <button onClick={() => setPaymentSuccess(null)} className="ml-auto text-green-400 text-lg">&times;</button>
          </div>
        )}

        {hasDelivery === false && (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-xl" data-testid="delivery-address-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Delivery Address Required</p>
                <p className="text-xs text-amber-600 dark:text-amber-300 mt-0.5">Add a delivery address in your profile before loading wallet credits.</p>
                <Button size="sm" onClick={() => navigate("/profile")} className="mt-2.5 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="go-to-profile-delivery-btn">
                  <MapPin size={12} className="mr-1.5" />Go to Profile
                </Button>
              </div>
            </div>
          </div>
        )}

        {hasBilling === false && (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-xl" data-testid="billing-address-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Billing Address Required</p>
                <p className="text-xs text-amber-600 dark:text-amber-300 mt-0.5">Add a billing address in your profile before loading wallet credits.</p>
                <Button size="sm" onClick={() => navigate("/profile")} className="mt-2.5 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="go-to-profile-btn">
                  <MapPin size={12} className="mr-1.5" />Go to Profile
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl" data-testid="wallet-tabs">
          <button className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "plans" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} onClick={() => setActiveTab("plans")} data-testid="tab-plans">
            Recharge Plans
          </button>
          <button className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "history" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} onClick={() => setActiveTab("history")} data-testid="tab-history">
            Transaction History
          </button>
        </div>

        {activeTab === "plans" && (
          <>
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Recharge Plans</h2>
            {loading ? (
              <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-32 rounded-xl"></div>)}</div>
            ) : plans.length === 0 ? (
              <div className="empty-state"><Wallet size={24} className="text-gray-300" /><p className="text-sm text-gray-500">No plans available</p></div>
            ) : (
              <div className="space-y-3">
                {plans.map((plan) => (
                  <div key={plan.plan_id} className={`plan-card ${plan.is_popular ? "popular" : ""}`} data-testid={`plan-${plan.plan_id}`}>
                    {plan.is_popular && <div className="bg-green-500 text-white text-[10px] font-bold px-3 py-1 rounded-full inline-block mb-3">Most Popular</div>}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                        <p className="text-[10px] text-gray-400">Pay {symbol}{Math.round(plan.amount)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-extrabold text-green-600">{Math.round(plan.credits)}</p>
                        <p className="text-[10px] text-gray-400">credits</p>
                      </div>
                    </div>
                    {plan.bonus_credits > 0 && <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-3 py-2 rounded-lg mb-3"><Gift size={14} /><span className="text-sm font-medium">+{Math.round(plan.bonus_credits)} bonus credits</span></div>}
                    {plan.expiry_days && <p className="text-[9px] text-gray-500 mb-3">Valid for {plan.expiry_days} days</p>}
                    {plan.tax_group && <p className="text-[9px] text-gray-500 mb-3">+ {plan.tax_group.taxes?.map(t => `${t.name} ${t.percentage}%`).join(", ")} applicable</p>}
                    <button className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl text-sm font-bold disabled:opacity-40"
                      onClick={() => handleRecharge(plan)} disabled={selectedPlan === plan.plan_id} data-testid={`recharge-${plan.plan_id}`}>
                      {selectedPlan === plan.plan_id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" />Processing...</> : (!hasDelivery || !hasBilling) ? `Add ${!hasDelivery ? "Delivery" : "Billing"} Address to Recharge` : "Recharge Now"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "history" && (
          <div data-testid="wallet-history">
            {txnLoading ? (
              <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-16 rounded-xl"></div>)}</div>
            ) : transactions.length === 0 ? (
              <div className="empty-state py-12"><Receipt size={24} className="text-gray-300" /><p className="text-sm text-gray-500">No transactions yet</p></div>
            ) : (
              <div className="space-y-5">
                {groupByMonth(transactions).map(([month, txns]) => (
                  <div key={month}>
                    <div className="flex items-center gap-3 mb-2.5">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{month}</p>
                      <div className="flex-1 h-px bg-gray-100"></div>
                    </div>
                    <div className="space-y-1.5">
                      {txns.map((t, idx) => (
                        <div key={t.txn_id || t.transaction_id || idx} className="flex items-center gap-3 px-3.5 py-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-50 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700 transition-colors" data-testid={`txn-${t.txn_id || t.transaction_id}`}>
                          {/* Left: icon + details stacked */}
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${getTypeBg(t.amount)}`}>
                            {t.amount >= 0 ? <ArrowDownLeft size={15} className="text-green-600" /> : <ArrowUpRight size={15} className="text-red-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{getTypeLabel(t.type)}</p>
                            <p className="text-[10px] text-gray-500 truncate">{t.note || t.description}</p>
                            <div className="mt-0.5">
                              <span className="text-[9px] text-gray-500">{formatDate(t.created_at)} &middot; {formatTime(t.created_at)}</span>
                              {t.type === "self_recharge" && t.reference_id && (
                                <button
                                  onClick={async () => {
                                    toast.info("Preparing invoice...");
                                    try {
                                      await downloadInvoicePdf("recharge", t.reference_id, `Receipt-${t.reference_id}.pdf`);
                                    } catch {
                                      toast.error("Failed to download invoice");
                                    }
                                  }}
                                  className="flex items-center gap-1 text-[10px] text-green-600 hover:text-green-700 font-semibold mt-0.5"
                                  data-testid={`download-invoice-${t.txn_id}`}
                                >
                                  <Download size={9} /> Download Invoice
                                </button>
                              )}
                            </div>
                          </div>
                          {/* Right: amount */}
                          <div className="text-right shrink-0 pl-2">
                            <p className={`text-base font-bold ${getTypeColor(t.type, t.amount)}`}>
                              {t.amount >= 0 ? "+" : ""}{symbol}{Math.abs(Math.round(t.amount))}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </UserLayout>
  );
}
